
import { useState, useEffect, useRef } from 'react';
import { Zap, Code, Users, CheckCircle2, Target } from 'lucide-react';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: <Target className="h-6 w-6 text-tech-purple" />,
      title: "Precision Focus",
      description: "We deliver solutions with meticulous attention to detail and technical excellence."
    },
    {
      icon: <CheckCircle2 className="h-6 w-6 text-tech-blue" />,
      title: "Quality Assurance",
      description: "Every project undergoes rigorous testing to ensure flawless performance."
    },
    {
      icon: <Users className="h-6 w-6 text-tech-cyan" />,
      title: "Client Partnership",
      description: "We build lasting relationships through transparency and collaborative success."
    },
    {
      icon: <Zap className="h-6 w-6 text-tech-pink" />,
      title: "Innovation First",
      description: "We constantly explore emerging technologies to keep your business ahead."
    }
  ];
  
  return (
    <section id="about" ref={sectionRef} className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className={isVisible ? "animate-fade-in" : "opacity-0"}>
            <div className="relative">
              <div className="glass-card rounded-2xl overflow-hidden shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1A1F2C] to-[#2D3748] mix-blend-overlay rounded-2xl">
                  {/* Animated code generation effect */}
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="code-line animate-slide-in-left delay-100 opacity-60 absolute top-[10%] left-4 text-xs text-[#0FA0CE] font-mono">
                      import React from &apos;react&apos;;
                    </div>
                    <div className="code-line animate-slide-in-left delay-200 opacity-60 absolute top-[15%] left-4 text-xs text-[#8E9196] font-mono">
                      const BakerTech = () =&gt; {'{'}
                    </div>
                    <div className="code-line animate-slide-in-left delay-300 opacity-60 absolute top-[20%] left-8 text-xs text-[#1EAEDB] font-mono">
                      // Building innovative solutions
                    </div>
                    <div className="code-line animate-slide-in-left delay-400 opacity-60 absolute top-[25%] left-8 text-xs text-[#fff] font-mono">
                      return &lt;TechSolution /&gt;;
                    </div>
                    <div className="code-line animate-slide-in-left delay-500 opacity-60 absolute top-[30%] left-4 text-xs text-[#8E9196] font-mono">
                      {'}'};
                    </div>
                    <div className="code-line animate-slide-in-left delay-600 opacity-60 absolute top-[35%] left-4 text-xs text-[#ea384c] font-mono">
                      function transform(digitalPresence) {'{'}
                    </div>
                    <div className="code-line animate-slide-in-left delay-700 opacity-60 absolute top-[40%] left-8 text-xs text-[#fff] font-mono">
                      return optimize(digitize(innovate()));
                    </div>
                    <div className="code-line animate-slide-in-left delay-800 opacity-60 absolute top-[45%] left-4 text-xs text-[#ea384c] font-mono">
                      {'}'};
                    </div>
                    <div className="code-line animate-pulse-slow absolute top-[55%] left-4 text-xs text-[#1EAEDB] font-mono">
                      $ running system check...
                    </div>
                    <div className="code-line animate-pulse-slow delay-1000 absolute top-[60%] left-4 text-xs text-[#1EAEDB] font-mono">
                      $ compiling resources...
                    </div>
                    <div className="code-line animate-pulse-slow delay-2000 absolute top-[65%] left-4 text-xs text-[#1EAEDB] font-mono">
                      $ deploying solutions...
                    </div>
                    <div className="code-line animate-pulse absolute top-[75%] left-4 text-xs text-[#0FA0CE] font-mono">
                      BUILD SUCCESSFUL
                    </div>
                    <div className="code-line animate-pulse absolute top-[80%] left-4 text-xs text-[#0FA0CE] font-mono">
                      DEPLOYMENT COMPLETE
                    </div>
                    
                    {/* Cursor animation */}
                    <div className="absolute top-[80%] left-[160px] h-3 w-2 bg-[#0FA0CE] animate-pulse"></div>
                  </div>
                </div>
                <div className="aspect-video bg-gradient-to-br from-[#1A1F2C]/90 to-[#2D3748]/90 rounded-2xl p-8 flex items-center justify-center">
                  <Code className="h-20 w-20 text-[#0FA0CE]" />
                </div>
              </div>
              <div className="absolute -bottom-5 -right-5 glass-card p-6 rounded-xl shadow-lg animate-float">
                <div className="flex items-center space-x-4">
                  <div className="bg-tech-purple/20 p-3 rounded-full">
                    <Zap className="h-6 w-6 text-tech-purple" />
                  </div>
                  <div>
                    <p className="font-bold">2 Years</p>
                    <p className="text-sm text-foreground/70">Tech Excellence</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className={isVisible ? "space-y-6 animate-fade-in-slow" : "space-y-6 opacity-0"}>
            <div className="inline-block rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              About BakerTech
            </div>
            <h2 className="text-3xl md:text-4xl font-bold">
              Powering Digital <span className="tech-gradient-text">Transformation</span>
            </h2>
            <p className="text-foreground/80">
              At BakerTech, we combine technical expertise with creative vision to deliver exceptional digital solutions. 
              Founded with a passion for technology and innovation, we've helped businesses of all sizes establish 
              and enhance their digital presence.
            </p>
            <p className="text-foreground/80">
              Our team of developers, designers, and digital strategists work collaboratively to create 
              tailored solutions that address your unique challenges and objectives. We don't just build 
              websites and software – we craft digital experiences that drive results.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
              {values.map((value, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="mt-1 bg-primary/10 p-2 rounded-full">
                    {value.icon}
                  </div>
                  <div>
                    <h3 className="font-bold">{value.title}</h3>
                    <p className="text-sm text-foreground/70">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
